import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight, ArrowRight } from "lucide-react";
import api from "../../lib/api";

/**
 * Component-level suggestions — where the founder should go on the app to
 * improve each component. Keeps the nudge card actionable rather than a
 * passive list of factor labels.
 *
 * Each entry uses `?focus=<field-id>` — the target page's <FocusHighlight>
 * hook picks it up, scrolls the field into view and pulses a gold ring
 * around the matching data-focus="…" element for 2 seconds.
 */
const COMPONENT_META = {
  company: {
    label: "Company Verification",
    cta: "Verify MCA",
    to: "/startup/profile?tab=company&focus=cin",
    tip: "Add your CIN so we can auto-fetch official MCA data.",
  },
  filings: {
    label: "Statutory Filings",
    cta: "Update Filings",
    to: "/startup/profile?tab=company&focus=filings",
    tip: "Recent AGM & balance-sheet filings signal a well-run company.",
  },
  financials: {
    label: "Financials",
    cta: "Add Financials",
    to: "/startup/financial-profile?focus=financials",
    tip: "Upload audited statements or InstaFinancials data.",
  },
  documentation: {
    label: "Documentation",
    cta: "Upload Docs",
    to: "/startup/documents?focus=upload",
    tip: "Pitch deck, cap table, ITR, GST — every doc adds trust.",
  },
  founder: {
    label: "Founder Profile",
    cta: "Complete Profile",
    to: "/startup/profile?tab=founder&focus=founder-name",
    tip: "Fill founder bios, LinkedIn, and KMP details.",
  },
  funding: {
    label: "Funding Ask",
    cta: "Clarify Ask",
    to: "/startup/funding-profile?focus=funding-ask",
    tip: "Add valuation, use-of-funds, and ticket range.",
  },
  debt: {
    label: "Debt Readiness",
    cta: "Add Debt Data",
    to: "/startup/financial-profile?focus=debt",
    tip: "Show receivables, cash flow & debt appetite.",
  },
  investor_fit: {
    label: "Investor Fit",
    cta: "Tune Preferences",
    to: "/startup/profile?tab=funding&focus=sector",
    tip: "Sharper sector & stage tags surface you to right investors.",
  },
  red_flags: {
    label: "Clean Baseline",
    cta: "Review Alerts",
    to: "/startup/dashboard?focus=alerts",
    tip: "Resolve any outstanding red flags flagged by our systems.",
  },
};

/**
 * TrustBoostNudge — actionable card that highlights the top 3 fastest wins
 * to boost the founder's VenturaZ Trust Score. Sourced from `/startup/trust-summary`
 * which returns per-component `factors[]` with `{ label, present, weight }`.
 */
export default function TrustBoostNudge() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/startup/trust-summary")
      .then((r) => setSummary(r.data))
      .catch(() => setErr(true));
  }, []);

  const nudges = useMemo(() => computeNudges(summary), [summary]);

  if (err) return null;
  if (!summary) {
    return (
      <div
        className="border border-navy/10 bg-white/70 backdrop-blur rounded-lg p-4 text-xs text-ink-muted"
        data-testid="trust-boost-loading"
      >
        Loading Trust Score suggestions…
      </div>
    );
  }
  if (summary.total >= 92) {
    // The founder is already elite — celebrate rather than nag.
    return (
      <div
        className="border border-emerald-300 bg-emerald-50/60 rounded-lg p-4 flex items-start gap-3"
        data-testid="trust-boost-elite"
      >
        <Sparkles size={16} className="text-emerald-700 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-heading text-emerald-900 text-sm">
            You&apos;re in the top tier · Trust Score {summary.total}/100
          </div>
          <div className="text-xs text-emerald-800 mt-0.5">
            Keep filings and financials fresh — investors prioritise recency next.
          </div>
        </div>
      </div>
    );
  }
  if (nudges.length === 0) {
    // API returned components but no missing factors — thank the founder
    // rather than render a blank card.
    return (
      <div
        className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-4 text-sm text-emerald-900"
        data-testid="trust-boost-complete"
      >
        <span className="font-heading">All key signals captured.</span> Keep filings and financials fresh — that&apos;s what investors compare on next.
      </div>
    );
  }

  return (
    <div
      className="border border-gold/40 bg-gradient-to-br from-white via-white to-gold/[0.06] rounded-lg p-5"
      data-testid="trust-boost-nudge"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-gold" />
        <div className="overline">Boost your Trust Score</div>
        <div className="ml-auto text-[11px] font-mono text-ink-muted">
          Current · {summary.total}/100
        </div>
      </div>
      <div className="text-sm text-ink-soft mb-4">
        Complete these {nudges.length} quick action{nudges.length === 1 ? "" : "s"} to unlock more visibility with matched investors.
      </div>
      <div className="space-y-2">
        {nudges.map((n) => (
          <Link
            key={n.componentKey + n.label}
            to={n.to}
            className="flex items-center justify-between gap-3 p-3 rounded-md border border-navy/10 hover:border-emerald-400/60 hover:bg-emerald-50/40 transition-colors"
            data-testid={`trust-nudge-${n.componentKey}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                {n.componentLabel} · +{n.points} pts
              </div>
              <div className="font-heading text-ink text-sm mt-0.5 truncate">{n.label}</div>
              <div className="text-[11px] text-ink-muted mt-0.5">{n.tip}</div>
            </div>
            <div className="text-xs text-emerald-700 font-heading whitespace-nowrap inline-flex items-center gap-1 flex-shrink-0">
              {n.cta} <ChevronRight size={12} />
            </div>
          </Link>
        ))}
      </div>
      <Link
        to="/startup/passport"
        className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80"
        data-testid="trust-boost-full-passport"
      >
        See full breakdown <ArrowRight size={12} />
      </Link>
    </div>
  );
}

/**
 * Pull the top 3 missing factors sorted by potential score contribution.
 * Each component's weight × factor's proportion of the component gives
 * an approximate "points you'll gain" number the founder can chase.
 */
function computeNudges(summary) {
  if (!summary?.components) return [];
  const items = [];
  Object.entries(summary.components).forEach(([key, comp]) => {
    const weight = summary.weights?.[key] || 0;
    const factors = comp.factors || [];
    const totalFactorWeight = factors.reduce((s, f) => s + (f.weight || 1), 0) || 1;
    factors.forEach((f) => {
      if (f.present) return;
      // Skip red_flags — those aren't things a founder can proactively "do".
      if (key === "red_flags") return;
      const points = Math.max(1, Math.round(((f.weight || 1) / totalFactorWeight) * weight));
      const meta = COMPONENT_META[key] || { label: key, cta: "Fix", to: "/startup/dashboard", tip: "" };
      items.push({
        componentKey: key,
        componentLabel: meta.label,
        label: f.label,
        points,
        cta: meta.cta,
        to: meta.to,
        tip: meta.tip,
      });
    });
  });
  // Sort by points DESC, then component label so ties are stable.
  items.sort((a, b) => b.points - a.points || a.componentLabel.localeCompare(b.componentLabel));
  return items.slice(0, 3);
}
